import { css } from '@emotion/react';
import { posthog } from 'posthog-js';
import { sub, format, startOfYear } from 'date-fns';
import { isNil, max, min, minBy } from 'lodash';
import { useCallback, useState, HTMLProps, FC } from 'react';
import { Spinner } from '@chakra-ui/react';
import {
  VictoryArea,
  VictoryPortal,
  VictoryCursorContainer,
  VictoryLine,
  VictoryScatter,
  LineSegment,
  VictoryGroup,
} from 'victory';
import { VictoryCommonPrimitiveProps } from 'victory-core';
import * as stores from '../../../stores';

import { TimeRange } from '../../../types';
import ErrorSection from '../../ErrorSection';

import { colors, fontStyles, spacing } from '../../../styles';
import Wiim from './Wiim';

export interface ChartNewsData extends ChartElement {
  x: number;
  y: number;
}

interface Props extends HTMLProps<HTMLDivElement> {
  strokeColor: string;
  charts?: Record<TimeRange, ChartElement[] | undefined>;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  timeRange?: TimeRange;
  selectedNewsElement?: ChartNewsData;
  onSelectNews: (chartElement?: ChartNewsData) => void;
  scannedPrice?: ChartNewsData;
  onScanPrice: (chartElement?: ChartNewsData | undefined) => void;
  ticker?: string;
  showWiim?: boolean;
  height?: number;
  /** When true, skips "no data before" calculation - used for Plaid adaptive charts */
  isAdaptiveChart?: boolean;
}

const Chart: FC<Props> = ({
  charts = {},
  isLoading,
  isError,
  onRetry,
  timeRange,
  strokeColor,
  selectedNewsElement,
  onSelectNews,
  scannedPrice,
  onScanPrice,
  ticker,
  showWiim = true,
  height = 200,
  isAdaptiveChart = false,
}) => {
  const [isTouchMoving, setIsTouchMoving] = useState<boolean>(false);
  const [noDateXPosition, setNoDateXPosition] = useState<number>(0);
  const selectedChart = (!isLoading && !isError && timeRange && charts?.[timeRange]) || [];
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);

  // Check if all chart data is empty (timeout or unavailable)
  const allChartsEmpty =
    !isLoading && !isError && charts && Object.values(charts).every((chart) => !chart || chart.length === 0);

  const fractionOfDomainWithData: number = Math.min(
    1,
    (() => {
      // Adaptive charts (like Plaid) use all available data without "no data" area
      if (isAdaptiveChart) return 1;
      if (!timeRange || selectedChart.length === 0 || timeRange === '1d') return 1;

      const expectedFirstDates: Record<TimeRange, Date> = {
        '1d': sub(new Date(), { days: 1 }),
        '1w': sub(new Date(), { days: 7 }), // matches backend
        '1m': sub(new Date(), { days: 28 }),
        '3m': sub(new Date(), { months: 3 }),
        '6m': sub(new Date(), { months: 6 }),
        ytd: startOfYear(new Date()),
        '1y': sub(new Date(), { years: 1 }),
        '5y': sub(new Date(), { years: 5 }),
      } as const;

      const fraction =
        (selectedChart.length > 0 ? new Date().getTime() - selectedChart[0].date.getTime() : 0) /
        (new Date().getTime() - expectedFirstDates[timeRange].getTime());

      // Chart layout constants
      const MIN_DATA_FRACTION = 0.7;
      const NEAR_COMPLETE_THRESHOLD = 0.95;

      // Limit the "no data" area to maximum 30% of the chart
      // This ensures that actual data takes at least 70% of the available space
      if (fraction < MIN_DATA_FRACTION) {
        return MIN_DATA_FRACTION;
      }

      // Allow for some slack if the ratio is close to 1.
      return fraction > NEAR_COMPLETE_THRESHOLD ? 1 : fraction;
    })(),
  );

  const shiftXValuesAmount = Math.floor(selectedChart.length / fractionOfDomainWithData - selectedChart.length);

  const chartData = selectedChart.map((element, index) => ({
    x: index + shiftXValuesAmount,
    y: element.price,
    articleHeadline: element.articleHeadline,
    price: element.price,
    benchmark: element.benchmark,
    changeRatio: element.changeRatio,
    date: element.date,
    minute: element.minute,
  }));

  const benchmarkData = chartData
    .filter((element) => !isNil(element.benchmark))
    .map((element) => ({
      ...element,
      y: element.benchmark!,
    }));

  const chartDataWithRatio = chartData.map((d) => ({ ...d, y: d.changeRatio ?? 0 }));
  const benchmarkDataWithPrice = benchmarkData.map((d) => ({ ...d, y: d.price }));

  const allYValues = chartDataWithRatio.map((d) => d.y);
  // const benchmarkYValues = benchmarkDataWithPrice.map((d) => d.y);

  const scatterData = chartDataWithRatio.filter((element) => element.articleHeadline);

  const formatTickLabel = (ratio?: number) => {
    if (ratio === undefined || isNaN(ratio)) return undefined;

    // Format as percentage with sign and 0 decimal places
    const percentage = (ratio * 100).toFixed(0);
    return `${ratio >= 0 ? '+' : ''}${percentage}%`;
  };

  const maxRatio = max(allYValues);
  const minRatio = min(allYValues);
  // const maxBenchmarkPrice = benchmarkDataWithPrice.length > 0 ? max(benchmarkYValues) : undefined;
  // const minBenchmarkPrice = benchmarkDataWithPrice.length > 0 ? min(benchmarkYValues) : undefined;

  const maxLabelValue = formatTickLabel(maxRatio);
  const minLabelValue = formatTickLabel(minRatio);

  const scanPrice = (xValue?: number) => {
    if (isNil(xValue)) {
      onSelectNews();
      onScanPrice();
      return;
    }

    const adjustedX = Math.round(xValue) - shiftXValuesAmount;
    const point = chartDataWithRatio[adjustedX];

    const maxDistance = chartDataWithRatio.length * 0.02;

    const closestPointWithArticleWithinDistance = minBy(
      scatterData.filter((element) => Math.abs(element.x - xValue) < maxDistance),
      (element) => Math.abs(element.x - xValue),
    );

    if (!isTouchMoving && !closestPointWithArticleWithinDistance) {
      onSelectNews();
      onScanPrice();
      return;
    }

    if (!isTouchMoving) {
      onScanPrice(closestPointWithArticleWithinDistance);
      onSelectNews(closestPointWithArticleWithinDistance);
    }

    onScanPrice(point);
    onSelectNews(closestPointWithArticleWithinDistance);
  };

  const subscriptionElement = (
    <>
      Historical insights are reserved for subscribers.{' '}
      <span
        onClick={() => stores.showPaymentModalByPreference('symbol_chart_learn_more')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && stores.showPaymentModalByPreference('symbol_chart_learn_more')}
        css={css`
          cursor: pointer;
          color: ${colors.petrolBlue};
          &:hover {
            opacity: 0.8;
          }
          html[data-theme='dark'] & {
            color: ${colors.skyBlue};
          }
        `}
      >
        Learn more
      </span>
    </>
  );

  const determineDotHighlighted = (data: VictoryCommonPrimitiveProps & { datum?: ChartNewsData }): boolean =>
    !!(selectedNewsElement?.articleHeadline && selectedNewsElement?.x === data.datum?.x);

  const noDateText = (() => {
    const date = chartData[0]?.date;
    if (!timeRange || shiftXValuesAmount === 0 || !date || timeRange === '1d') return '';
    const mapping: Record<TimeRange, string> = {
      '1d': '', // handled by early return above
      '1w': format(date, 'MMMM d'),
      '1m': format(date, 'MMMM d'),
      '3m': format(date, 'MMMM d'),
      '6m': format(date, 'MMMM d'),
      ytd: format(date, 'MMMM d'),
      '1y': format(date, 'MMMM y'),
      '5y': format(date, 'MMMM y'),
    };

    return `No data before ${mapping[timeRange]}`;
  })();

  const noDateRef = useCallback((node) => {
    if (node !== null) {
      setNoDateXPosition(node.getBoundingClientRect().x);
    }
  }, []);

  const isFreeTicker = (ticker?: string): boolean => {
    if (!ticker) return false;
    return ['AAPL', 'TSLA'].includes(ticker.toUpperCase());
  };

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      {(isError || isLoading || allChartsEmpty) && (
        <div
          css={css`
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 1;
          `}
        >
          {isError ? (
            <ErrorSection text="Problem fetching chart data." onRetry={onRetry} />
          ) : allChartsEmpty ? (
            <ErrorSection text="Charts temporarily unavailable. Please try again." onRetry={onRetry} />
          ) : (
            <Spinner color={colors.cloud} size="lg" />
          )}
        </div>
      )}
      {showWiim && (
        <div
          css={css`
            height: 60px;
            overflow: hidden;
            margin-bottom: ${spacing.centi};
            position: relative;
            z-index: 20;
          `}
        >
          {selectedNewsElement?.articleHeadline && (
            <Wiim
              text={
                purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive || isFreeTicker(ticker)
                  ? selectedNewsElement.articleHeadline
                  : subscriptionElement
              }
              isLatest={false}
              textLength={
                typeof selectedNewsElement.articleHeadline === 'string' ? selectedNewsElement.articleHeadline.length : 0
              }
            />
          )}
        </div>
      )}
      <div
        css={css`
          margin-top: 0;
          margin-bottom: ${spacing.milli};
          color: ${colors.silver};
          visibility: ${maxLabelValue ? 'visible' : 'hidden'};
          ${fontStyles.flea};
        `}
      >
        {maxLabelValue || '123 skeleton'}
      </div>
      <div
        css={css`
          position: relative;
          left: -${spacing.deci};
          width: calc(100% + ${spacing.deci} + ${spacing.deci});
          max-width: 600px;
          -webkit-tap-highlight-color: transparent;
          -webkit-touch-callout: none; /* iOS Safari */
          -webkit-user-select: none; /* Safari */
        `}
        onTouchStart={() => {
          setIsTouchMoving(false);
          posthog.capture('Touched Chart');
        }}
        onTouchMove={() => {
          setIsTouchMoving(true);
        }}
        onTouchEnd={() => {
          setIsTouchMoving(false);
          if (isTouchMoving) scanPrice();
        }}
      >
        {shiftXValuesAmount > 0 && (
          <div
            css={css`
              z-index: 1;
              position: absolute;
              top: 50%;
              right: ${fractionOfDomainWithData * 100}%;
              padding: 0 ${spacing.deci};
              transform: translateY(-50%);
              color: ${colors.silver};
              visibility: ${noDateXPosition < 0 ? 'hidden' : 'visible'};
              ${fontStyles.flea}
            `}
            ref={noDateRef}
          >
            {noDateText}
          </div>
        )}
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore: Suppress complex union type error from VictoryGroup children */}
        <VictoryGroup
          padding={0}
          height={height}
          domainPadding={{ y: 5 }}
          domain={
            chartDataWithRatio.length > 1
              ? {
                  x: [0, chartDataWithRatio[chartDataWithRatio.length - 1].x],
                  y:
                    minRatio === undefined || maxRatio === undefined || minRatio === maxRatio
                      ? undefined
                      : [minRatio, maxRatio],
                }
              : undefined
          }
          singleQuadrantDomainPadding={false}
          containerComponent={
            <VictoryCursorContainer
              cursorDimension="x"
              onCursorChange={(value: any) => scanPrice(value)}
              cursorComponent={<LineSegment style={{ stroke: scannedPrice ? colors.cloud : 'transparent' }} />}
              style={{ touchAction: 'auto' }}
            />
          }
        >
          <VictoryLine
            name="line"
            data={chartDataWithRatio}
            interpolation="monotoneX"
            style={{
              data: {
                stroke: strokeColor,
                strokeWidth: '3px',
                strokeLinejoin: 'round',
                strokeLinecap: 'round',
              },
            }}
          />
          {benchmarkData.length > 0 && (
            <VictoryLine
              name="benchmark-line"
              data={benchmarkDataWithPrice}
              interpolation="monotoneX"
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              y={(_datum) => {
                return 0;
              }}
              style={{
                data: {
                  stroke: colors.silver,
                  strokeWidth: '2px',
                  strokeDasharray: '4, 4',
                },
              }}
            />
          )}
          {shiftXValuesAmount > 0 && chartDataWithRatio.length > 0 && (
            <VictoryArea
              data={[
                { x: 0, y: maxRatio },
                { x: chartDataWithRatio[0].x, y: maxRatio },
              ]}
              style={{
                data: {
                  fill: colors.fog,
                },
              }}
            />
          )}
          {shiftXValuesAmount > 0 && chartDataWithRatio.length > 0 && (
            <VictoryLine
              data={[
                { x: chartDataWithRatio[0].x, y: minRatio },
                { x: chartDataWithRatio[0].x, y: maxRatio },
              ]}
              style={{
                data: { stroke: colors.cloud, strokeDasharray: '7' },
              }}
            />
          )}
          <VictoryPortal>
            <VictoryScatter
              data={scatterData}
              size={(data) => (determineDotHighlighted(data) ? 8 : 6)}
              style={{
                data: {
                  fill: (data) => (determineDotHighlighted(data) ? colors.petrolBlue : colors.sugar),
                  stroke: colors.petrolBlue,
                  strokeWidth: '3px',
                },
              }}
            />
          </VictoryPortal>
        </VictoryGroup>
      </div>
      <div
        css={css`
          margin-top: ${spacing.milli};
          margin-bottom: ${spacing.regular};
          color: ${colors.silver};
          visibility: ${minLabelValue ? 'visible' : 'hidden'};
          ${fontStyles.flea};
        `}
      >
        {minLabelValue || '123 skeleton'}
      </div>
    </div>
  );
};

export default Chart;
